import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import tibcosService from '../services/tibcosService'
import { blockchainValueToNumber } from '../utils/blockchainMonitor'

export interface BlockchainBlock extends Record<string, unknown> {
  number?: unknown
  timestamp?: unknown
  hash?: string
  parentHash?: string
  parentInfo?: Array<{ blockHash?: string }>
  transactions?: unknown[]
  gasUsed?: unknown
  sealer?: unknown
  version?: unknown
}

export interface MonitoredTransaction {
  hash: string
  from?: string
  to?: string
  blockNumber: number
  blockTimestamp?: unknown
  index: number
  raw: unknown
}

interface BlockchainMonitorValue {
  connected: boolean
  loading: boolean
  refreshing: boolean
  latestBlockNumber: number
  chainVersion: string
  latestBlock: BlockchainBlock | null
  recentBlocks: BlockchainBlock[]
  recentTransactions: MonitoredTransaction[]
  lastUpdatedAt: number | null
  newBlockAt: number | null
  error: string | null
  refresh: () => Promise<void>
}

const BlockchainMonitorContext = createContext<BlockchainMonitorValue | null>(null)

const collectRecentTransactions = (blocks: BlockchainBlock[]): MonitoredTransaction[] =>
  blocks
    .flatMap((block) => {
      const blockNumber = blockchainValueToNumber(block.number)
      const transactions = Array.isArray(block.transactions) ? block.transactions : []
      return transactions.map((transaction: unknown, index: number) => {
        const detail =
          typeof transaction === 'object' && transaction !== null
            ? (transaction as Record<string, unknown>)
            : null
        return {
          hash: String(detail?.hash ?? detail?.transactionHash ?? transaction ?? ''),
          from: detail?.from ? String(detail.from) : undefined,
          to: detail?.to ? String(detail.to) : undefined,
          blockNumber,
          blockTimestamp: block.timestamp,
          index,
          raw: transaction,
        }
      })
    })
    .filter((transaction) => transaction.hash)
    .slice(0, 12)

export interface BlockchainMonitorProviderProps {
  children: React.ReactNode
  pollIntervalMs?: number
  recentBlockLimit?: number
}

export const BlockchainMonitorProvider: React.FC<BlockchainMonitorProviderProps> = ({
  children,
  pollIntervalMs = 3000,
  recentBlockLimit = 6,
}) => {
  const [connected, setConnected] = useState(false)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [latestBlockNumber, setLatestBlockNumber] = useState(0)
  const [chainVersion, setChainVersion] = useState('')
  const [latestBlock, setLatestBlock] = useState<BlockchainBlock | null>(null)
  const [recentBlocks, setRecentBlocks] = useState<BlockchainBlock[]>([])
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null)
  const [newBlockAt, setNewBlockAt] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  const mountedRef = useRef(false)
  const inFlightRef = useRef(false)
  const latestHeightRef = useRef<number | null>(null)
  const recentBlocksRef = useRef<BlockchainBlock[]>([])

  const loadSnapshot = useCallback(
    async (forceBlockRefresh = false) => {
      if (inFlightRef.current) return
      inFlightRef.current = true
      if (mountedRef.current) setRefreshing(true)

      try {
        const [height, version] = await Promise.all([
          tibcosService.getBlockNumber(),
          tibcosService.getClientVersion(),
        ])
        const previousHeight = latestHeightRef.current
        const shouldRefreshBlocks =
          forceBlockRefresh || previousHeight !== height || recentBlocksRef.current.length === 0

        let nextBlocks = recentBlocksRef.current
        if (shouldRefreshBlocks) {
          try {
            nextBlocks = await tibcosService.getRecentBlocks(recentBlockLimit, true)
          } catch {
            const currentBlock = await tibcosService.getBlockByNumber(height, true)
            nextBlocks = [currentBlock]
          }
        }

        if (!mountedRef.current) return

        if (previousHeight !== null && previousHeight !== height) setNewBlockAt(Date.now())
        latestHeightRef.current = height
        recentBlocksRef.current = nextBlocks

        const currentBlock =
          nextBlocks.find((block) => blockchainValueToNumber(block.number) === height) ??
          nextBlocks[0] ??
          null
        setConnected(true)
        setLatestBlockNumber(height)
        setChainVersion(version)
        setRecentBlocks(nextBlocks)
        setLatestBlock(currentBlock)
        setLastUpdatedAt(Date.now())
        setError(null)
      } catch (requestError) {
        if (!mountedRef.current) return
        const responseStatus =
          typeof requestError === 'object' && requestError !== null && 'response' in requestError
            ? (requestError as { response?: { status?: number } }).response?.status
            : undefined
        setConnected(false)
        setLastUpdatedAt(Date.now())
        setError(
          responseStatus === 401 || responseStatus === 403
            ? '登录后可查看链上状态'
            : '暂时无法读取区块链网络',
        )
      } finally {
        inFlightRef.current = false
        if (mountedRef.current) {
          setLoading(false)
          setRefreshing(false)
        }
      }
    },
    [recentBlockLimit],
  )

  useEffect(() => {
    mountedRef.current = true
    void loadSnapshot()
    const interval = window.setInterval(() => void loadSnapshot(), pollIntervalMs)
    return () => {
      mountedRef.current = false
      window.clearInterval(interval)
    }
  }, [loadSnapshot, pollIntervalMs])

  const refresh = useCallback(() => loadSnapshot(true), [loadSnapshot])
  const recentTransactions = useMemo(() => collectRecentTransactions(recentBlocks), [recentBlocks])

  const value = useMemo<BlockchainMonitorValue>(
    () => ({
      connected,
      loading,
      refreshing,
      latestBlockNumber,
      chainVersion,
      latestBlock,
      recentBlocks,
      recentTransactions,
      lastUpdatedAt,
      newBlockAt,
      error,
      refresh,
    }),
    [
      chainVersion,
      connected,
      error,
      lastUpdatedAt,
      latestBlock,
      latestBlockNumber,
      loading,
      newBlockAt,
      recentBlocks,
      recentTransactions,
      refresh,
      refreshing,
    ],
  )

  return (
    <BlockchainMonitorContext.Provider value={value}>{children}</BlockchainMonitorContext.Provider>
  )
}

export const useBlockchainMonitor = (): BlockchainMonitorValue => {
  const context = useContext(BlockchainMonitorContext)
  if (!context) {
    throw new Error('useBlockchainMonitor must be used within BlockchainMonitorProvider')
  }
  return context
}
